import React, { useCallback, useContext, useMemo, useState } from "react";
import { uuidv7 } from "uuidv7";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { APIProcessName } from "@common/constants/process-names";
import { pluralizeWithCount } from "@common/helpers/string";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Alert } from "@design-system/Alert/Alert";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ApplyCoaPreviewTable } from "@components/settings/ChartOfAccountsTemplates/apply/ApplyCoaPreviewTable";
import { ApplyCoaTemplatePreviewShell } from "@components/settings/ChartOfAccountsTemplates/apply/ApplyCoaTemplatePreviewShell";
import { mapApplyEventToHistoricalCoaInputs } from "@components/settings/ChartOfAccountsTemplates/apply/applyCoaTemplateHistorySnapshotAdapter";
import { useWorkspacesList } from "@api/workspace.api";
import { buildTemplateActivityQueryKey } from "@api/coaTemplateActivity.api";
import { useScope } from "@state/scopeContext.lib";
import { SubScopeProvider } from "@state/subScopeContext";
import { historyDrawerContext } from "../../historyDrawerContext";
import { getActivityDescription } from "../../utils/getActivityDescription";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import { buildCoaTemplateActivityEntries } from "./coaTemplateActivityEntries";
import { resolveCoaTemplateEventRefs } from "./coaTemplateActivityEvents";
import {
    buildRevertedActivityRow,
    getRevertedEntityIds,
} from "./buildRevertedActivityRow";
import { getRevertedPreviewNotice } from "./coaRevertedActivityCopy";
import { getUndoSubtitleSuffix } from "./coaUndoAvailability";
import { buildCoaUndoCommand } from "./coaUndoCommand";
import { buildUndoDisabledReasons } from "./coaUndoDisabledReason";
import {
    getGroupedUndoSummary,
    getGroupedUndoToast,
} from "./coaTemplateGroupedUndo";
import { CoaTemplateApplyUndoFooter } from "./CoaTemplateApplyUndoFooter";
import { useCoaTemplateActivityEvents } from "./useCoaTemplateActivityEvents";
import { useCoaTemplateActivitySelection } from "./useCoaTemplateActivitySelection";
import { useCoaTemplateGroupedUndo } from "./useCoaTemplateGroupedUndo";
import { getCoaTemplatePreviewSubtitle } from "./coaTemplatePreviewSubtitle";
import styles from "./CoaTemplateApplyHistoryDrawer.module.scss";

export const CoaTemplateApplyHistoryDrawer: React.FC = () => {
    const { open, historyRow, openDrawer, closeDrawer } =
        useContext(historyDrawerContext);
    const { workspaceId, organizationId } = useScope();
    const toaster = useToaster();
    const [isOpen, setIsOpen] = useState(false);
    const shouldShowContent = isOpen || open;
    const eventRefs = useMemo(
        () =>
            resolveCoaTemplateEventRefs({
                historyRow,
                currentWorkspaceId: workspaceId,
            }),
        [historyRow, workspaceId],
    );
    const eventsQuery = useCoaTemplateActivityEvents({
        eventRefs,
        enabled: shouldShowContent,
    });
    const { data: workspaces = [], isLoading: areWorkspacesLoading } =
        useWorkspacesList(
            {
                organizationId: organizationId ?? undefined,
                includeEntities: true,
            },
            { enabled: shouldShowContent },
        );
    const entries = useMemo(
        () => buildCoaTemplateActivityEntries({ eventRefs, workspaces }),
        [eventRefs, workspaces],
    );
    const workspaceRolesById = useMemo(
        () =>
            new Map(
                workspaces.map((workspace) => [
                    workspace.id,
                    workspace.access.workspaceRole,
                ]),
            ),
        [workspaces],
    );
    const isApplySource =
        historyRow?.sourceType ===
        APIProcessName.CHART_OF_ACCOUNTS_TEMPLATE_APPLY;
    const isReadOnly = !isApplySource;
    const selection = useCoaTemplateActivitySelection({
        eventRefs,
        events: eventsQuery.data,
        isReadOnly,
        workspaceRolesById,
    });
    const events = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);
    const activeEvent = events.find(
        (event) => event.eventRef.eventId === selection.activeEventRef?.eventId,
    );
    const activeEventDetail =
        activeEvent?.status === "loaded" ? activeEvent.detail : undefined;
    const activeViewModel = activeEventDetail
        ? mapApplyEventToHistoricalCoaInputs(activeEventDetail)
        : null;
    const activeEntry = entries.find(
        (entry) => entry.entityId === selection.activeEventRef?.entityId,
    );
    const revertedEntityIds = useMemo(
        () => getRevertedEntityIds(events),
        [events],
    );
    const entityNamesById = useMemo(
        () => new Map(entries.map((entry) => [entry.entityId, entry.name])),
        [entries],
    );
    // Read-only rails render no controls, so they need no per-entity reasons.
    const undoDisabledReasons = useMemo(
        () =>
            isReadOnly
                ? new Map<number, string>()
                : buildUndoDisabledReasons({ events, workspaceRolesById }),
        [events, isReadOnly, workspaceRolesById],
    );
    const templateActivityQueryKey = buildTemplateActivityQueryKey({
        organizationId,
        templateId: activeEventDetail?.templateId,
    });
    const undoMutation = useCoaTemplateGroupedUndo({
        templateActivityQueryKey,
    });
    const undoCommand = isReadOnly
        ? null
        : buildCoaUndoCommand({
              events,
              selectedEventRefs: selection.selectedEventRefs,
              entries,
              workspaceRolesById,
              isUndoPending: undoMutation.isPending,
          });
    // "View reverted activity" only applies to an Applied event that was later
    // reverted, and only when a deterministic Reverted row can be built from
    // legacy metadata. The Reverted source drawer never offers it (no recursion).
    const revertedActivityRow =
        isApplySource && activeEventDetail?.revertedAt
            ? buildRevertedActivityRow({
                  activeDetail: activeEventDetail,
                  events,
              })
            : null;
    const onViewReverted = revertedActivityRow
        ? () => openDrawer(revertedActivityRow)
        : null;
    const handleClose = useCallback(() => closeDrawer(), [closeDrawer]);
    const handleConfirmUndo = useCallback(async () => {
        if (selection.selectedEventRefs.length === 0) {
            return;
        }
        const results = await undoMutation.mutateAsync({
            eventRefs: selection.selectedEventRefs,
            undoInvocationId: uuidv7(),
        });
        const { message, severity, isPersistent } = getGroupedUndoToast({
            summary: getGroupedUndoSummary(results),
            entityNamesById,
        });
        toaster.toast(message, undefined, {
            variant: severity,
            preventAutoClose: isPersistent,
        });
    }, [entityNamesById, selection.selectedEventRefs, undoMutation, toaster]);
    const clientCount = new Set(entries.map((entry) => entry.workspaceId)).size;
    const subtitleWarning = getUndoSubtitleSuffix({
        events,
        isReadOnly,
        workspaceRolesById,
    });
    const activityTitle = historyRow ? getActivityDescription(historyRow) : "";
    const isLoading = eventsQuery.isLoading || areWorkspacesLoading;
    const { refetch: refetchEvents } = eventsQuery;
    const handleRetry = useCallback(() => {
        void refetchEvents();
    }, [refetchEvents]);
    // Remount the table per event so expansion, search, and filters start from
    // the newly active event's own defaults instead of inheriting the previous
    // entity's state.
    // The preview shows what the apply once changed, so a reverted entity needs it
    // labelled as history. The Reverted drawer already says so in its own header.
    const revertedPreviewNotice =
        isApplySource && activeEventDetail
            ? getRevertedPreviewNotice(activeEventDetail)
            : null;
    const preview = renderPreview({
        viewModel: activeViewModel,
        eventId: activeEvent?.eventRef.eventId,
        revertedNotice: revertedPreviewNotice,
        onRetry: handleRetry,
    });
    const body = renderBody({
        isLoading,
        isError: eventsQuery.isError,
        onRetry: handleRetry,
        shell: (
            <ApplyCoaTemplatePreviewShell
                entries={entries}
                activeEntityId={selection.activeEventRef?.entityId ?? null}
                selectedEntityIds={selection.selectedEntityIds}
                disabledEntityIds={selection.disabledEntityIds}
                disabledReasonsByEntityId={undoDisabledReasons}
                revertedEntityIds={revertedEntityIds}
                selectionMode={isReadOnly ? "readOnly" : "undo"}
                title="Chart of Accounts template preview"
                onActiveEntityChange={selection.setActiveEntityId}
                onSelectedEntityIdsChange={selection.setSelectedEntityIds}
                preview={
                    selection.activeEventRef ? (
                        <SubScopeProvider
                            value={{
                                workspaceId:
                                    selection.activeEventRef.workspaceId,
                            }}
                        >
                            {activeEntry ? preview : null}
                        </SubScopeProvider>
                    ) : null
                }
                footer={
                    <CoaTemplateApplyUndoFooter
                        onClose={handleClose}
                        undoCommand={undoCommand}
                        onConfirmUndo={handleConfirmUndo}
                        onViewReverted={onViewReverted}
                    />
                }
            />
        ),
    });

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft={
                historyRow ? (
                    <ActivityDrawerHeader
                        historyRow={historyRow}
                        activityTitle={activityTitle}
                    />
                ) : null
            }
            size="fullScreenWithNav"
            noPadding
        >
            {shouldShowContent && historyRow && (
                <div className={styles.drawerContent}>
                    <div className={styles.subtitle}>
                        {getCoaTemplatePreviewSubtitle(historyRow.sourceType)}
                        {!areWorkspacesLoading && (
                            <>
                                {" · "}
                                {pluralizeWithCount(
                                    "entity",
                                    entries.length,
                                )}{" "}
                                across{" "}
                                {pluralizeWithCount("client", clientCount)}
                            </>
                        )}
                        {subtitleWarning && (
                            <span
                                className={styles.subtitleWarning}
                                data-testid="coa-undo-subtitle-warning"
                            >
                                {" "}
                                {subtitleWarning}
                            </span>
                        )}
                    </div>
                    {body}
                </div>
            )}
        </Drawer>
    );
};

function renderBody({
    isLoading,
    isError,
    onRetry,
    shell,
}: {
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    shell: React.ReactNode;
}) {
    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader />
            </div>
        );
    }
    if (isError) {
        return (
            <div className={styles.stateContainer}>
                <EmptyState
                    header="Couldn't load this activity"
                    body="Something went wrong while loading what was applied."
                    ctaText="Try again"
                    action={onRetry}
                />
            </div>
        );
    }
    return shell;
}

function renderPreview({
    viewModel,
    eventId,
    revertedNotice,
    onRetry,
}: {
    viewModel: ReturnType<typeof mapApplyEventToHistoricalCoaInputs> | null;
    eventId: string | undefined;
    revertedNotice: string | null;
    onRetry: () => void;
}) {
    // A single event can fail while the aggregate resolves, so this per-event
    // failure gets the same retry affordance as a whole-aggregate failure. The
    // aggregate refetch is what reloads it.
    if (!viewModel) {
        return (
            <EmptyState
                header="Couldn't load this activity"
                body="Something went wrong while loading what was applied."
                ctaText="Try again"
                action={onRetry}
            />
        );
    }
    if (viewModel.isEmpty) {
        return withRevertedNotice(
            revertedNotice,
            <EmptyState header="No changes" />,
        );
    }
    if (viewModel.accounts.length === 0 && viewModel.groups.length === 0) {
        return withRevertedNotice(
            revertedNotice,
            <EmptyState header="No account changes" />,
        );
    }
    return withRevertedNotice(
        revertedNotice,
        <ApplyCoaPreviewTable
            key={eventId}
            mode="historical"
            accounts={viewModel.accounts}
            groups={viewModel.groups}
            newGroupIds={viewModel.newGroupIds}
        />,
    );
}

// Only the reverted case gains the extra flex column, so an eligible preview keeps
// its existing single-child layout untouched.
function withRevertedNotice(notice: string | null, content: React.ReactNode) {
    if (!notice) {
        return content;
    }
    return (
        <div className={styles.previewWithNotice}>
            <div
                className={styles.revertedPreviewNotice}
                data-testid="coa-reverted-preview-notice"
            >
                <Alert variant="warning">{notice}</Alert>
            </div>
            <div className={styles.previewNoticeContent}>{content}</div>
        </div>
    );
}
